<template>
  <div class="registration" redesign>
    <!-- Header -->
    <StepProgressBar v-if="employeeCode" step3 />
    <div v-else class="local-hero">
      <h1 class="local-hero-header" v-translate>Account creation</h1>
    </div>

    <div v-if="!employeeCode" class="registration-form form-container">
      <p>
        <translate>Please browse to the</translate>
        <span>&nbsp;</span>
        <router-link :to="{ name: 'Verification' }" v-translate>verification page</router-link>
        <span>&nbsp;</span>
        <translate>to sign up.</translate>
      </p>
    </div>
    <div v-else class="registration-form form-container">
      <div class="registration-form-header form-header" v-translate>
        Enter your information below to create an account.
      </div>
      <LoadSpinner v-if="pageLoading" :color="organizationDetails.darkBrandColor" />
      <form v-else @submit.prevent="handleSubmit">
        <div class="row-wrapper">
          <!-- First name -->
          <FormTextField
            id="firstName"
            label="First name"
            :tabIndex="1"
            :value.sync="firstName"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('firstName')"
            required
          />

          <!-- Last name -->
          <FormTextField
            id="lastName"
            label="Last name"
            :tabIndex="2"
            :value.sync="lastName"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('lastName')"
            required
          />
        </div>

        <div class="row-wrapper">
          <!-- Email -->
          <FormTextField
            id="email"
            label="Personal email address"
            helpText="example@email.com"
            :tabIndex="3"
            :value.sync="email"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('email')"
            required
          />

          <!-- Confirm email -->
          <FormTextField
            id="confirmEmail"
            label="Re-enter personal email address"
            helpText="example@email.com"
            :tabIndex="4"
            :value.sync="confirmEmail"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('emailsMatch')"
            required
          />
        </div>

        <!-- Password restrictions -->
        <div class="form-field info-container">
          <div class="tooltip-icon">
            <img src="@/assets/icons/information-icon.png" />
          </div>
          <div class="tooltip">
            <h4 class="tooltip-header" v-translate>
              Passwords must meet the following requirements:
            </h4>
            <span class="tooltip-text" v-translate>passwordRequirements</span>
          </div>
        </div>

        <div class="row-wrapper">
          <!-- Password -->
          <FormTextField
            id="password"
            type="password"
            label="Password"
            :tabIndex="5"
            :value.sync="password"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('password')"
            required
          />

          <!-- Confirm password -->
          <FormTextField
            id="verifiedPassword"
            type="password"
            label="Re-enter password"
            :tabIndex="6"
            :value.sync="verifiedPassword"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('passwordsMatch')"
            required
          />
        </div>

        <div class="row-wrapper">
          <!-- Birth year -->
          <FormTextField
            id="birthYear"
            label="Year of birth"
            helpText="YYYY"
            :tabIndex="7"
            :value.sync="birthYear"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('birthYearValid')"
            :required="employeeSchema?.hasOwnProperty('birthYear')"
          />

          <!-- Zip code -->
          <FormTextField
            id="zipCode"
            label="Zip code"
            helpText="#####"
            :tabIndex="8"
            :value.sync="zipCode"
            :showErrorMessage="hasSubmitted"
            :errorMessage="fieldErrorMessage('zipCode')"
            :required="employeeSchema?.hasOwnProperty('zipCode')"
          />
        </div>

        <!-- Language preferences -->
        <div v-if="enableLangPref" class="form-field required">
          <h4 class="form-field-header" v-translate>
            What is your preferred language for communication?
          </h4>
          <div class="form-field-container">
            <div :class="{ 'md-invalid': this.errors || !this.preferredLanguage }">
              <div class="radio-buttons">
                <label class="radio-button">
                  <input
                    type="radio"
                    id="preferredLanguage"
                    name="preferredLanguage"
                    v-model="preferredLanguage"
                    value="en"
                  />
                  <span v-translate>English</span>
                </label>
                <label class="radio-button">
                  <input
                    type="radio"
                    name="preferredLanguage"
                    v-model="preferredLanguage"
                    value="es"
                  />
                  <span v-translate>Spanish</span>
                </label>
              </div>
            </div>
          </div>
          <div class="error-text">
            <span>{{ fieldErrorMessage('preferredLanguage') }}</span>
          </div>
        </div>

        <!-- Communication preferences -->
        <div v-if="enableSms" class="form-field required">
          <h4 class="form-field-header" v-translate>communicationPreferences</h4>
          <div class="form-field-container">
            <div
              :class="{
                'md-invalid': this.errors || (!this.enableSmsAlerts && !this.enableEmailAlerts),
              }"
            >
              <div class="checkboxes">
                <label class="checkbox">
                  <input
                    type="checkbox"
                    id="emailAlerts"
                    name="emailAlerts"
                    v-model="enableEmailAlerts"
                  />
                  <span class="form-checkbox" />
                  <span v-translate>Email</span>
                </label>
                <label class="checkbox">
                  <input
                    type="checkbox"
                    id="smsAlerts"
                    name="smsAlerts"
                    v-model="enableSmsAlerts"
                  />
                  <span class="form-checkbox" />
                  <span v-translate>SMS</span>
                </label>
              </div>
            </div>
          </div>
          <div class="error-text">
            <span>
              {{ fieldErrorMessage('communicationPrefs') }}
            </span>
          </div>
        </div>

        <!-- Phone number -->
        <FormTextField
          v-if="enableSms"
          id="phoneNumber"
          class="sms-phone-number"
          label="Phone number"
          helpText="(###)###-####"
          :tabIndex="9"
          :value.sync="phoneNumber"
          :showErrorMessage="hasSubmitted"
          :errorMessage="fieldErrorMessage('phoneNumber')"
          :required="enablePhoneContact || enableSmsAlerts"
          mask="(###) ###-####"
        />

        <!-- Phone contact settings -->
        <div v-if="enableSms" class="form-field required">
          <h4 class="form-field-header" v-translate>
            Can we reach out to you by phone if we need more information from you to process your
            request?
          </h4>
          <div class="form-field-container">
            <div class="radio-buttons">
              <label class="radio-button">
                <input
                  type="radio"
                  id="phonePrefs"
                  name="enablePhoneContact"
                  v-model="enablePhoneContact"
                  :value="true"
                />
                <span v-translate>Yes</span>
              </label>
              <label class="radio-button">
                <input
                  type="radio"
                  name="enablePhoneContact"
                  v-model="enablePhoneContact"
                  :value="false"
                />
                <span v-translate>No</span>
              </label>
            </div>
          </div>
        </div>

        <!-- GC T&C -->
        <div class="form-field">
          <div class="form-field-container terms-and-conditions">
            <div class="checkboxes">
              <label class="checkbox">
                <input type="checkbox" id="termsAndConditions" v-model="checked" />
                <span class="form-checkbox" />
                <span>
                  <span v-translate>I agree to the</span>&nbsp;<a
                    :href="tcLink"
                    target="_blank"
                    v-translate
                    >GCTandC</a
                  >.
                </span>
              </label>
            </div>
          </div>
          <div class="help-text">
            <div v-if="checked !== true" :class="['ruby-font']">
              * <span>{{ $gettext('Required') }}</span>
            </div>
          </div>
        </div>

        <!-- Next step -->
        <div class="registration-form-footer form-footer">
          <div class="next-step">
            <translate>Next step:</translate><br /><translate class="next-step-name"
              >Personal Information</translate
            >
          </div>
        </div>

        <!-- Submit -->
        <div class="button-container">
          <md-button type="submit" tabindex="10" class="form-button" :style="organizationCssVars">
            <span v-if="!loading" key="next" v-translate>Next</span>
            <span v-else-if="loading" key="spinner">
              <LoadSpinner :color="'white'" />
            </span>
          </md-button>
        </div>
      </form>
    </div>

    <Modal
      v-if="enableSmsModal"
      dimmed
      :additionalModalStyles="{
        width: '100%',
        'max-width': '600px',
        border: 'none',
        padding: 0,
        overflow: 'hidden scroll',
      }"
      :additionalContainerStyles="{
        zIndex: '1001',
        padding: 0,
      }"
      @close-modal="enableSmsModal = false"
    >
      <div class="form-container card-shape modal-window">
        <div class="form-field">
          <h2 class="form-field-header" v-translate>Enable SMS Notifications</h2>
          <div class="form-field-container">
            <p v-translate>We want to process your grant request as quickly as possible. Enabling SMS notifications allows us to get you money faster.</p>
            <p v-translate>Would you like to sign up to receive SMS alerts when we're trying to contact you?</p>
          </div>
        </div>
        <div class="button-container" style="justify-content: space-between">
          <button class="form-button white-button" @click="enableSmsModal = false; handleSubmit()" v-translate>No, keep them off</button>
          <button class="form-button" @click="enableSmsAlerts = true; enableSmsModal = false; handleSubmit()" v-translate>Yes, enable SMS notifications</button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadSpinner from '@/components/LoadSpinner'
import { objectToCamelCase } from '@/services/utils'
import Employee from '@/services/employees'
import StepProgressBar from '@/components/stepper/StepProgressBar'
import FormTextField from '@/components/forms/FormTextField'
import { analyticsMixin } from '@/services/googleAnalytics'
import FAQ from '@/services/faqs'
import PinwheelApi from '@/services/pinwheel'
import NomineesApi from '@/services/nominees'
import Modal from '@/components/Modal'

export default {
  name: 'Registration',
  mixins: [analyticsMixin],
  components: {
    StepProgressBar,
    LoadSpinner,
    FormTextField,
    Modal,
  },
  data() {
    return {
      employeeCode: null,
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      password: '',
      verifiedPassword: '',
      birthYear: '',
      zipCode: '',
      checked: false,
      errors: null,
      hasSubmitted: false,
      loading: false,
      pageLoading: false,
      enableLangPref: this.$store.state.organizationDetails.enableLanguagePref,
      enableSms: true,
      enableSmsAlerts: false,
      enableEmailAlerts: true,
      enablePhoneContact: false,
      phoneNumber: '',
      preferredLanguage: this.$language.current,
      clientErrors: {},
      employeeSchema: null,
      tcLink: null,
      pinwheelAccountId: '',
      pinwheelStartDate: '',
      nominatedBy: '',
      subOrgId: null,
      enableSmsModal: false,
    }
  },
  async created() {
    this.pageLoading = true
    this.employeeCode = this.$route.params.code
    this.subOrgId = this.$route.params.subOrgId

    /* If a legacy user was sent up from the API, pre-populate that info into our form */
    if (this.employeeCode && this.employeeCode.legacyUser) {
      const legacy = this.employeeCode.legacyUser
      this.firstName = legacy.firstName
      this.lastName = legacy.lastName
      this.email = legacy.email
      this.birthYear = legacy.birthYear
    }

    await this.prefillRegistrationFields()

    await this.getEmployeeSchema()
    FAQ.api.getTcOrTaxFaq(this.organizationDetails.name, 'tc').then(faq => {
      this.tcLink = `/faq#${faq.faqOrder}`
    })
    this.pageLoading = false
  },
  methods: {
    async getEmployeeSchema() {
      await Employee.api.schema().then(res => {
        this.employeeSchema = res['actions']['POST']
      })
    },

    async createEmployeeAccount({
      firstName,
      lastName,
      email,
      confirmEmail,
      password,
      birthYear,
      zipCode,
      code,
      preferredLanguage,
      enableSmsAlerts,
      enableEmailAlerts,
      enablePhoneContact,
      phoneNumber,
      pinwheelAccountId,
      pinwheelStartDate,
      nominatedBy,
      subOrgId,
    }) {
      this.loading = true
      this.errors = null
      const hasSetNotifPreferences = enableSmsAlerts || enableEmailAlerts
      this.trackClickedCTA()
      await Employee.api
        .registerEmployee(
          firstName,
          lastName,
          email,
          confirmEmail,
          password,
          birthYear || null,
          zipCode,
          code,
          preferredLanguage,
          enableSmsAlerts,
          enableEmailAlerts,
          enablePhoneContact,
          hasSetNotifPreferences,
          phoneNumber,
          pinwheelAccountId,
          pinwheelStartDate,
          nominatedBy,
          subOrgId,
        )
        .then(res => {
          const employee = Employee.fromAPI(res.data)
          this.$store.dispatch('updateUserToken', res.data.token)
          this.$store.dispatch('updateUser', employee)
          this.$store.commit('UPDATE_CURRENT_LANGUAGE', employee.preferredLanguage)
        })
        .catch(err => {
          this.eventsTracker('personal-information-submission', err.response.data)
          this.errors = objectToCamelCase(err.response.data)
        })
      this.loading = false
      if (this.errors === null && this.verifiedPassword !== '' && this.formValid()) {
        this.navigateToSurveys()
      } else {
        this.formValid()
      }
    },
    navigateToSurveys() {
      if (this.phoneNumber.length && this.enableSmsAlerts) {
        this.$router.push({
          name: 'Validation',
          params: {
            routeName: this.nextRoute(),
            code: this.employeeCode,
          },
        })
      }
      if (!this.phoneNumber.length || !this.enableSmsAlerts) {
        this.$router.push({
          name: this.nextRoute(),
          params: {
            code: this.employeeCode,
          },
        })
      }
    },
    nextRoute() {
      if (this.organizationDetails.isTcrOrganization) {
        // TCR Upward Fund onboarding
        return 'NomineeOnboardingImpactStatement'
      } else {
        // If no onboarding surveys need to be done, go to the home page
        return 'Home'
      }
    },
    handleSubmit() {
      // Enable SMS notifications modal
      if(this.enableSms && !this.enableSmsAlerts && !this.hasSubmitted) {
        this.enableSmsModal = true;
        this.hasSubmitted = true;
        return false
      }

      this.hasSubmitted = true
      this.errors = null

      if (this.checked === true && this.formValid()) {
        let employeeAccount = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          confirmEmail: this.confirmEmail,
          password: this.password,
          birthYear: this.birthYear || null,
          zipCode: this.zipCode,
          preferredLanguage: this.enableLangPref ? this.preferredLanguage : this.currentLanguage,
          enableSmsAlerts: this.enableSmsAlerts,
          enableEmailAlerts: this.enableEmailAlerts,
          enablePhoneContact: this.enablePhoneContact,
          phoneNumber: this.phoneNumber,
          subOrgId: this.subOrgId,
        }

        if (this.isEmployeeCode)
          employeeAccount['code'] = this.employeeCode.code

        if (this.isPinwheelApi) {
          employeeAccount['pinwheelAccountId'] = this.employeeCode
          if (this.pinwheelStartDate == '') {
            let today = new Date()
            let today_iso = today.toISOString().substr(0, 10)
            employeeAccount['pinwheelStartDate'] = today_iso
          } else {
            employeeAccount['pinwheelStartDate'] = this.pinwheelStartDate
          }

          employeeAccount['code'] = this.employeeCode
        }

        this.createEmployeeAccount(employeeAccount)
        if(this.formValid())
          return true
      }

      return false
    },
    fieldErrorMessage(field) {
      switch (field) {
        case 'firstName':
          if (this.firstName === '') return this.$gettext('First name may not be blank')
          break

        case 'lastName':
          if (this.lastName === '') return this.$gettext('Last name may not be blank')
          break

        case 'email':
          if (this.errors && this.errors.email) return this.errors.email[0]
          /*
          var error_msg = (this.errors && this.errors.email && this.errors.email[0]) ||
            (this.clientErrors.email && this.clientErrors.email[0])
          error_msg = error_msg === undefined ? '' : error_msg
          return error_msg
          */
          break

        case 'emailsMatch':
          if (this.email.length && !this.emailsMatch) return this.$gettext('Must match')
          break

        case 'password':
          if (this.isNotValidPassword || !this.passwordLongEnough)
            return this.$gettext(
              'Your password does not meet the password requirements outlined above.',
            )
          break

        case 'passwordsMatch':
          if (!this.passwordsMatch) return this.$gettext('Must match')
          break

        case 'birthYearValid':
          if (!this.birthYearValid) return this.$gettext('Birth year invalid')
          break

        case 'zipCode':
          if (!this.zipCodeValid)
            return (
              (this.errors && this.errors.zipCode && this.errors.zipCode[0]) ||
              (this.clientErrors.zipCode && this.clientErrors.zipCode[0]) ||
              this.$gettext('Invalid')
            )
          break

        case 'phoneNumber':
          if((this.enableSmsAlerts || this.enablePhoneContact) && this.phoneNumber === '') {
            return this.$gettext('You must enter your phone number.')
          }
          var error_msg =
            (this.errors && this.errors.phoneNumber && this.errors.phoneNumber[0]) ||
            (this.clientErrors.phoneNumber && this.clientErrors.phoneNumber[0])
          error_msg = error_msg === undefined ? '' : error_msg
          return error_msg

          break

        case 'preferredLanguage':
          if (!this.preferredLanguage) return this.$gettext('Please select your preferred language')
          break

        case 'communicationPrefs':
          if (!this.enableSmsAlerts && !this.enableEmailAlerts)
            return this.$gettext('Please select at least one of the options available')
          break

        default:
          break
      }

      return ''
    },
    formValid() {
      if (this.fieldErrorMessage('firstName') !== '') {
        document.getElementById('firstName').focus()
        return false
      }

      if (this.fieldErrorMessage('lastName') !== '') {
        document.getElementById('lastName').focus()
        return false
      }

      if (this.fieldErrorMessage('email') !== '') {
        document.getElementById('email').focus()
        return false
      }

      if (this.fieldErrorMessage('emailsMatch') !== '') {
        document.getElementById('confirmEmail').focus()
        return false
      }

      if (this.fieldErrorMessage('password') !== '') {
        document.getElementById('password').focus()
        return false
      }

      if (this.fieldErrorMessage('passwordsMatch') !== '') {
        document.getElementById('verifiedPassword').focus()
        return false
      }

      if (this.fieldErrorMessage('birthYearValid') !== '') {
        document.getElementById('birthYear').focus()
        return false
      }

      if (this.fieldErrorMessage('zipCode') !== '') {
        document.getElementById('zipCode').focus()
        return false
      }

      if (this.fieldErrorMessage('preferredLanguage') !== '') {
        document.getElementById('preferredLanguage').focus()
        return false
      }

      if (this.fieldErrorMessage('communicationPrefs') !== '') {
        document.getElementById('emailAlerts').focus()
        return false
      }

      if (this.fieldErrorMessage('phoneNumber') !== '') {
        document.getElementById('phoneNumber').focus()
        return false
      }

      if (this.checked === false) {
        document.getElementById('termsAndConditions').focus()
        return false
      }

      return true
    },
    async prefillRegistrationFields() {
      this.pageLoading = true
      await this.handlePrefillPinwheel()
      await this.handlePrefillTCR()
      this.pageLoading = false
    },
    async handlePrefillPinwheel() {
      // If using Pinwheel API, fetch the user's account information to pre-fill
      if (this.isPinwheelApi) {
        this.pinwheelAccountId = this.employeeCode

        let pinwheel_account = await PinwheelApi.getIdentity(this.pinwheelAccountId, { timeout: 5000 })
        if (pinwheel_account && pinwheel_account.data) {
          // Fill in name fields
          if (pinwheel_account.data.full_name !== '') {
            let full_name_split = pinwheel_account.data.full_name.split(' ')
            if (full_name_split.length == 2) {
              this.firstName = full_name_split[0]
              this.lastName = full_name_split[1]
            }
          }

          // Fill in email fields
          if (pinwheel_account.data.emails.length) {
            this.email = pinwheel_account.data.emails[0]
            this.confirmEmail = pinwheel_account.data.emails[0]
          }

          // Fill in year of birth
          if (pinwheel_account.data.date_of_birth && pinwheel_account.data.date_of_birth !== '')
            this.birthYear = pinwheel_account.data.date_of_birth.substring(0, 4)

          // Fill in zip code
          if (pinwheel_account.data.address && pinwheel_account.data.address.postal_code !== '')
            this.zipCode = pinwheel_account.data.address.postal_code

          // Fill in phone number
          if (pinwheel_account.data.phone_numbers && pinwheel_account.data.phone_numbers.length) {
            if (pinwheel_account.data.phone_numbers[0].value.length == 12) {
              let phone_number = pinwheel_account.data.phone_numbers[0].value.substring(2)
              let area_code = phone_number.substr(0, 3)
              let exchange_code = phone_number.substr(3, 3)
              let subscriber_number = phone_number.substr(6, 4)
              this.phoneNumber = '(' + area_code + ')' + exchange_code + '-' + subscriber_number
            }
          }
        }

        // Get the user's start date from Pinwheel API
        let pinwheel_employment = await PinwheelApi.getEmployment(this.pinwheelAccountId, { timeout: 5000 })
        if (pinwheel_employment && pinwheel_employment.data) this.pinwheelStartDate = pinwheel_employment.data.start_date
      }
    },
    async handlePrefillTCR() {
      // If TCR, pre-fill what we can from the nomination survey
      if (
        this.employeeCode &&
        this.employeeCode.code &&
        this.organizationDetails.isTcrOrganization
      ) {
        this.nominatedBy = this.employeeCode.nominatedBy

        let nomination_data = await NomineesApi.getNominationData(this.employeeCode.code)

        // Make sure we received the nomination data before pre-filling the registration
        if (nomination_data && nomination_data.success) {
          // Fill in first name
          if (nomination_data.first_name) this.firstName = nomination_data.first_name

          // Fill in last name
          if (nomination_data.last_name) this.lastName = nomination_data.last_name

          // Fill in email
          if (nomination_data.email) {
            this.email = nomination_data.email
            this.confirmEmail = nomination_data.email
          }

          // Fill in phone number
          if (nomination_data.phone_number) this.phoneNumber = nomination_data.phone_number
        }
      }
    },
    isVerificationMethod(method_name) {
      return ((!this.organizationDetails.subOrgVerificationMethodOverride && this.organizationDetails.verificationMethod == method_name) ||
        (this.organizationDetails.subOrgVerificationMethodOverride && this.selectedSubOrg?.verificationMethod == method_name))
    },
  },
  computed: {
    ...mapState(['organizationDetails', 'organizationCssVars', 'currentLanguage']),
    isNotValidPassword() {
      let regex_pass = new RegExp(
        '(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[~!@#$%^&;*()_+{}":])(?=.{10,})',
      )

      return this.password.length > 0 && !regex_pass.test(this.password)
    },
    passwordLongEnough() {
      return this.password.length >= 10
    },
    passwordsMatch() {
      return this.password == this.verifiedPassword
    },
    emailsMatch() {
      return this.email === this.confirmEmail
    },
    birthYearValid() {
      const today = new Date()
      const birthYearRange = this.birthYear < today.getFullYear() && this.birthYear > 1900

      if (this.birthYear.length > 0) {
        return birthYearRange
      }

      if (
        this.birthYear.length === 0 &&
        this.employeeSchema &&
        !this.employeeSchema.hasOwnProperty('birthYear')
      ) {
        return true
      }
    },
    phoneNumberValid() {
      if (!this.enableSms) {
        return true
      }
      if (this.phoneNumber.length === 0) {
        return true
      }
      if (
        this.userEdited.phoneNumber.length <= 14 &&
        phoneRegex.test(this.userEdited.phoneNumber)
      ) {
        return true
      }
      return false
    },
    zipCodeValid() {
      const zipRegex = new RegExp(/^[0-9]{5}?$/)

      if (this.zipCode.length === 0 && !this.hasSubmitted) {
        return true
      }

      if (
        this.zipCode.length === 0 &&
        this.employeeSchema &&
        !this.employeeSchema.hasOwnProperty('zipCode')
      ) {
        return true
      }

      const zipValid =
        this.zipCode &&
        this.zipCode.length > 1 &&
        (this.zipCode.length === 5 || this.zipCode.length === 10) &&
        !(this.errors && this.errors.zipCode) &&
        zipRegex.test(this.zipCode)

      if (!zipValid) {
        this.clientErrors = {
          ...this.clientErrors,
          zipCode: [
            this.$gettext(
              'Please enter a valid, 5-digit zip code. If your zip code is less than five digits add leading zeros (e.g. 00123)',
            ),
          ],
        }

        return false
      } else {
        this.clientErrors = { ...this.clientErrors, zipCode: [] }

        return true
      }
    },
    isEmployeeCode() {
      return this.isVerificationMethod('EMPLOYEE_CODE')
    },
    isPinwheelApi() {
      return this.isVerificationMethod('PINWHEEL_API')
    },
    selectedSubOrg() {
      if(this.subOrgId)
        return this.organizationDetails.subOrganizations.find(subOrg => subOrg.id == this.subOrgId)
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/responsive';
@import '@/styles/flex-box';

.registration {
  display: flex;
  flex-direction: column;

  .registration-form {
    @extend .constrain-tablet-and-up;

    @include tablet-and-up-screen {
      .registration-form-header {
        text-align: center;
      }
    }

    .sms-phone-number {
      margin-left: 2.5rem;
      max-width: 420px;

      @include tablet-screen {
        max-width: 344px;
      }
    }

    .terms-and-conditions {
      background-color: rgba(0, 42, 139, 0.1);
      border-radius: 12px;
      padding: 1.5rem;

      .checkbox {
        margin-bottom: 0;
      }

      span.form-checkbox {
        display: inline-block;
        vertical-align: top;

        & + span {
          display: inline-block;
          width: 90%;
        }
      }
    }

    .ruby-font {
      color: $ruby;
    }
  }
}
</style>
